import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Compass,
  LayoutDashboard,
  List,
  Coins,
  ClipboardList,
  Sparkles,
  type LucideIcon,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface Step {
  icon: LucideIcon
  titleKey: string
  bodyKey: string
}

const STEPS: Step[] = [
  {
    icon: Compass,
    titleKey: 'quickTour.welcomeTitle',
    bodyKey: 'quickTour.welcomeBody',
  },
  {
    icon: LayoutDashboard,
    titleKey: 'quickTour.dashboardTitle',
    bodyKey: 'quickTour.dashboardBody',
  },
  {
    icon: List,
    titleKey: 'quickTour.campsTitle',
    bodyKey: 'quickTour.campsBody',
  },
  {
    icon: Coins,
    titleKey: 'quickTour.revenueTitle',
    bodyKey: 'quickTour.revenueBody',
  },
  {
    icon: ClipboardList,
    titleKey: 'quickTour.formsTitle',
    bodyKey: 'quickTour.formsBody',
  },
  {
    icon: Sparkles,
    titleKey: 'quickTour.aiTitle',
    bodyKey: 'quickTour.aiBody',
  },
]

export function QuickTour({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  const [index, setIndex] = useState(0)
  const step = STEPS[index]
  const isFirst = index === 0
  const isLast = index === STEPS.length - 1
  const Icon = step.icon

  function finish() {
    setIndex(0)
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish() }}>
      <DialogContent className="sm:max-w-[460px] bg-surface border-border">
        <div className="flex flex-col items-center text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-accent/10">
            <Icon className="size-6 text-accent" />
          </div>
          <DialogTitle className="mt-3 text-base font-semibold text-text">
            {t(step.titleKey)}
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm leading-relaxed text-text-2">
            {t(step.bodyKey)}
          </DialogDescription>

          {/* Progress dots */}
          <div className="mt-5 flex gap-1.5">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  i === index ? 'w-5 bg-accent' : 'w-1.5 bg-surface-3',
                )}
              />
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" className="text-text-3" onClick={finish}>
            {isLast ? t('quickTour.close') : t('quickTour.skip')}
          </Button>
          <div className="flex gap-2">
            {!isFirst && (
              <Button variant="outline" size="sm" onClick={() => setIndex((i) => i - 1)}>
                {t('quickTour.back')}
              </Button>
            )}
            {isLast ? (
              <Button size="sm" onClick={finish}>{t('quickTour.start')}</Button>
            ) : (
              <Button size="sm" onClick={() => setIndex((i) => i + 1)}>
                {t('quickTour.next', { current: index + 1, total: STEPS.length })}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
